<div class="noc-policy">
  <article class="noc-policy__article shadow-md">
    <h1 class="noc-policy__heading noc-policy__heading--title">
      Network Operations
    </h1>

    <section>
      <h2 id="what-we-do" class="noc-policy__heading">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'what-we-do'"
        >
          What we do
        </span>
      </h2>

      <p class="noc-policy__text">
        The Network Operations Center (NOC) strives to put customer satisfaction
        at the forefront of every aspect of our support model. Our NOC would be
        the first point of contact for any issues that may arise with your
        service after the service has been installed. This would include
        addressing any problems negatively impacting your service, change
        management, coordinating maintenance activity and analyzing and
        responding to deviations in our network performance monitoring.
      </p>
      <p class="noc-policy__text">
        Our focus is clear and consistent communication, timely updates, and
        one-time resolution of any problems that may arise. Our technicians are
        available 24x7x365 to assist you in any way we can with the vast
        majority of our calls being answered and attended to in under 20
        seconds.
      </p>
      <p class="noc-policy__text">
        We look forward to helping you in any way we can!
      </p>
    </section>

    <section>
      <h2 id="contact-us" class="noc-policy__heading">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'contact-us'"
        >
          Contact Us
        </span>
      </h2>

      <div
        class="mb-3 w-min max-w-full overflow-x-auto overflow-y-hidden whitespace-nowrap rounded-md border border-gray-300"
      >
        <table
          class="overflow-hidden rounded-md border text-[var(--noc-policy--color-text)]"
        >
          <tr>
            <td class="border border-gray-300 px-3 py-2">Phone Number</td>
            <td class="border border-gray-300 px-3 py-2">
              <a href="tel:+18668920915" class="noc-policy__link"
                >+1 866 892 0915</a
              >
            </td>
          </tr>
          <tr class="bg-[var(--noc-policy--color-text--0025)]">
            <td class="border border-gray-300 px-3 py-2">
              Outage/Technical Issues
            </td>
            <td class="border border-gray-300 px-3 py-2">
              <a href="mailto:CBNnoc@comcast.com" class="noc-policy__link"
                >CBNnoc&#64;comcast.com</a
              >
            </td>
          </tr>
          <tr>
            <td class="border border-gray-300 px-3 py-2">Change Management</td>
            <td class="border border-gray-300 px-3 py-2">
              <a href="mailto:CBNsupport@comcast.com" class="noc-policy__link"
                >CBNsupport&#64;comcast.com</a
              >
            </td>
          </tr>
        </table>
      </div>

      <p class="noc-policy__text italic">
        Please dial in for all urgent issues and Escalation requests to ensure a
        timely response.
      </p>

      <h3 id="noc-contacts" class="noc-policy__heading italic">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'noc-contacts'"
        >
          NOC Contacts & Escalation Procedures
        </span>
      </h3>

      <div class="mb-3">
        <app-severity-level-contacts-grid
          [severityLevelContacts]="nocSeverityLevelContacts"
        />
      </div>

      <h3 id="msc-contacts" class="noc-policy__heading italic">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'msc-contacts'"
        >
          MSC Contacts & Escalation Procedures
        </span>
      </h3>

      <div class="mb-3">
        <app-severity-level-contacts-grid
          [severityLevelContacts]="mscSeverityLevelContacts"
        />
      </div>

      <h3 id="severity-definitions" class="noc-policy__heading italic">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'severity-definitions'"
        >
          Severity Definitions
        </span>
      </h3>

      <div class="text-[var(--noc-policy--color-text)]">
        <p id="5th-level">
          <span
            class="noc-policy__referenceable"
            [routerLink]="[]"
            [fragment]="'5th-level'"
          >
            <strong>Severity 5:&nbsp;</strong>
            <span class="opacity-70">(Highest)</span>
          </span>
        </p>
        <ul class="noc-policy__text ml-4 list-inside list-disc">
          <li>Critical network failure</li>
          <li>Entire network or systems down or inoperative</li>
          <li>Service affecting event &mdash; Severe business impact</li>
          <li>Continuous network performance impairment</li>
          <li>
            <span class="font-semibold">Resolution ETAs:</span>
            <ul class="ml-4 list-inside list-disc">
              <li><b>Tech on Duty:</b> Immediate</li>
              <li><b>Sr. Tech on Duty:</b> 1 hour</li>
              <li><b>Manager:</b> 4 hours</li>
              <li><b>VP Level:</b> 16 hours</li>
            </ul>
          </li>
        </ul>

        <p id="4th-level">
          <span
            class="noc-policy__referenceable"
            [routerLink]="[]"
            [fragment]="'4th-level'"
          >
            <strong>Severity 4:&nbsp;</strong>
            <span class="opacity-70">(High)</span>
          </span>
        </p>
        <ul class="noc-policy__text ml-4 list-inside list-disc">
          <li>Significant network degradation</li>
          <li>Service affecting &mdash; business impact</li>
          <li>
            <span class="font-semibold">Resolution ETAs:</span>
            <ul class="ml-4 list-inside list-disc">
              <li><b>Tech on Duty:</b> Immediate</li>
              <li><b>Sr. Tech on Duty:</b> 4 hours</li>
              <li><b>Manager:</b> 8 hours</li>
              <li><b>VP Level:</b> 48 hours</li>
            </ul>
          </li>
        </ul>

        <p id="3rd-level">
          <span
            class="noc-policy__referenceable"
            [routerLink]="[]"
            [fragment]="'3rd-level'"
          >
            <strong>Severity 3:&nbsp;</strong>
            <span class="opacity-70">(Normal)</span>
          </span>
        </p>
        <ul class="noc-policy__text ml-4 list-inside list-disc">
          <li>Minor network or system event/impairment</li>
          <li>Service affecting &mdash; minimal business impact</li>
          <li>Low priority</li>
          <li>
            <span class="font-semibold">Resolution ETAs:</span>
            <ul class="ml-4 list-inside list-disc">
              <li><b>Tech on Duty:</b> Immediate</li>
              <li><b>Sr. Tech on Duty:</b> 48 hours</li>
              <li><b>Manager:</b> As Needed</li>
              <li><b>VP Level:</b> As Needed</li>
            </ul>
          </li>
        </ul>

        <p id="2nd-level">
          <span
            class="noc-policy__referenceable"
            [routerLink]="[]"
            [fragment]="'2nd-level'"
          >
            <strong>Severity 2:&nbsp;</strong>
            <span class="opacity-70">(Low)</span>
          </span>
        </p>
        <ul class="noc-policy__text ml-4 list-inside list-disc">
          <li>Passive network or system issue/event</li>
          <li>Configuration and Design</li>
          <li>General Question</li>
          <li>Change Control</li>
          <li>
            <span class="font-semibold">Resolution ETAs:</span>
            <ul class="ml-4 list-inside list-disc">
              <li><b>Tech on Duty:</b> Immediate</li>
              <li><b>Sr. Tech on Duty:</b> As Needed</li>
              <li><b>Manager:</b> As Needed</li>
              <li><b>VP Level:</b> As Needed</li>
            </ul>
          </li>
        </ul>
      </div>
    </section>

    <section>
      <h2 id="proactive-monitoring" class="noc-policy__heading">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'proactive-monitoring'"
        >
          Proactive Monitoring and Notification Standards
        </span>
      </h2>
      <p class="noc-policy__text">
        We proactively monitor Node/Device reachability of our managed CPE
        device every five (5) minutes with a failure being recognized if the
        device misses three (3) consecutive polling intervals. If there is a
        node reachability, a trouble ticket will automatically open and send an
        e-mail alert to the customer's Primary Outage Contact with the trouble
        ticket and a note for the customer to confirm the outage.
      </p>
      <p class="noc-policy__text">
        This ticket will be set in to a “Pending Customer” status until the
        customer confirms the outage is genuine, and there is no local power
        issue or inside wiring work being performed at the customer premise.
        Once the customer confirms there is a genuine service issue, they should
        contact the NOC via phone, while referencing the Proactive Ticket
        number. The Proactive Ticket will then go in to a “Working” status, and
        the NOC will move forward to troubleshoot and isolate the problem. This
        change in status marks the beginning of the time interval for issue
        resolution.
      </p>
      <p class="noc-policy__text">
        If the customer does not notify the NOC of any service affecting
        condition or issue, the trouble ticket will be automatically closed in
        24 hours from the time of the initial notification if the service issues
        resolves. If the site remains unreachable, our ticket will remain open
        pending customer confirmation.
      </p>
      <ul class="noc-policy__text ml-4 list-inside list-disc">
        <li>
          Our NOC will require a site Local Contact name, phone number and site
          access-hours to open a ticket with our underlying vendors for
          investigation.
        </li>
        <li>
          Power should be verified to all equipment attached to the service.
        </li>
      </ul>
    </section>

    <section>
      <h2 id="change-management" class="noc-policy__heading">
        <span
          class="noc-policy__referenceable"
          [routerLink]="[]"
          [fragment]="'change-management'"
        >
          Change Management (Support)
        </span>
      </h2>
      <ul class="noc-policy__text ml-4 list-inside list-disc">
        <li>
          All non-service affecting change requests should be submitted via
          email to
          <a href="mailto:CBNsupport@comcast.com" class="noc-policy__link"
            >CBNsupport&#64;comcast.com</a
          >.
        </li>
        <li>
          Non-service affecting change management events are defined as items
          such as CPE Configuration Changes, Firewall Changes, Routing Changes,
          and Utilization Report Requests.
        </li>
        <li>
          These non-service affecting change requests are managed by the Support
          Team during normal business hours of 8:00am to 8:00pm Central, Monday
          – Friday.
        </li>
        <li>
          An acknowledgement of receipt for the change management request will
          be made within 2 hours.
        </li>
        <li>
          Most change management request are completed within 24 &ndash; 72
          hours, with Expedites being completed same day.
        </li>
        <li>
          If additional time is needed to process your request, Support
          <a href="mailto:CBNsupport@comcast.com" class="noc-policy__link"
            >CBNsupport&#64;comcast.com</a
          >
          will provide more details regarding any delays.
        </li>
        <li>
          Change Requests requested outside of the normal business hours of
          8:00am to 8:00pm Central, Monday &ndash; Friday will be subject to
          additional professional services fees.
        </li>
      </ul>
    </section>
    <footer class="mt-20 text-end">
      <span class="text-[var(--noc-policy--color-orange)]">
        smarter technology made <strong>simple</strong>
      </span>
    </footer>
  </article>
</div>
